import {
  contrastRatio,
  hexToRgb,
  oklchToRgb,
  rgbTo255,
  rgbToOklch,
  type Rgb,
} from "./colorMath";

// Single source of truth for the default brand colors (RevShare violet).
// Replaces the literals previously duplicated across themeUtils/getColors,
// AppearanceSettings and the store/detail pages.
export const DEFAULT_BRAND = {
  primary: "#7C3AED",
  secondary: "#E8E9FF",
};

export type BrandColors = { primary: string; secondary?: string | null };

const SHADES = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const;
type Shade = (typeof SHADES)[number];

// Per-shade (lightness, chroma ratio vs the 600 anchor) in OKLCH, sampled from
// the stock Tailwind violet ramp so deriveBrandScale(DEFAULT_BRAND.primary)
// reproduces violet-50..900 within 1/255 per channel.
const LIGHT_TARGETS: Record<Shade, [number, number]> = {
  50: [0.9691, 0.0651],
  100: [0.9433, 0.1152],
  200: [0.8943, 0.2226],
  300: [0.8112, 0.4108],
  400: [0.709, 0.6455],
  500: [0.6056, 0.8878],
  600: [0.5413, 1], // L is overridden by the (clamped) anchor lightness
  700: [0.4907, 0.9783],
  800: [0.432, 0.854],
  900: [0.3796, 0.7231],
};

// Dark-mode ladder. Shade roles after the class migration: 50-200 are tinted
// surfaces (must go dark), 300-500 decorative/rings, 600 solid fills + links
// (slightly lifted so it doesn't sink into the dark background), 700 hover,
// 800-900 text sitting on the 50-200 tints (must go light).
const DARK_TARGETS: Record<Shade, [number, number]> = {
  50: [0.23, 0.3],
  100: [0.27, 0.4],
  200: [0.33, 0.5],
  300: [0.45, 0.7],
  400: [0.58, 0.9],
  500: [0.65, 1],
  600: [0.6, 1], // L is max(anchor, 0.58)
  700: [0.52, 0.95],
  800: [0.8, 0.45],
  900: [0.88, 0.3],
};

const WHITE: Rgb = [1, 1, 1];
const GRAY_900: Rgb = [17 / 255, 24 / 255, 39 / 255];

type Triplet = [number, number, number];
export type BrandScale = {
  shades: Record<Shade, Triplet>;
  contrast: Triplet; // foreground for text on brand-600
};

function buildScale(anchorHex: string, mode: "light" | "dark"): BrandScale | null {
  const rgb = hexToRgb(anchorHex);
  if (!rgb) return null;
  const anchor = rgbToOklch(rgb);
  // Keep 600 usable as a fill under white/near-white text and keep the ladder
  // monotonic even for pastel or near-black inputs.
  const anchorL = Math.min(0.62, Math.max(0.45, anchor.l));
  const targets = mode === "light" ? LIGHT_TARGETS : DARK_TARGETS;

  const shades = {} as Record<Shade, Triplet>;
  for (const shade of SHADES) {
    const [tl, cRatio] = targets[shade];
    const l = shade === 600 ? (mode === "light" ? anchorL : Math.max(anchorL, 0.58)) : tl;
    shades[shade] = rgbTo255(oklchToRgb({ l, c: anchor.c * cRatio, h: anchor.h }));
  }

  const six = shades[600].map((v) => v / 255) as Rgb;
  const contrast =
    contrastRatio(WHITE, six) >= contrastRatio(GRAY_900, six) ? WHITE : GRAY_900;
  return { shades, contrast: rgbTo255(contrast) };
}

function secondaryTriplets(hex: string): { light: Triplet; dark: Triplet } | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const lch = rgbToOklch(rgb);
  return {
    light: rgbTo255(rgb),
    // Dark counterpart of the tint: same hue, dark low-chroma surface.
    dark: rgbTo255(oklchToRgb({ l: 0.3, c: Math.min(lch.c, 0.05), h: lch.h })),
  };
}

const asVar = (t: Triplet) => t.join(" ");

function forEachVar(
  scale: { light: BrandScale; dark: BrandScale },
  cb: (name: string, value: string) => void
) {
  for (const shade of SHADES) {
    cb(`--brand-${shade}-l`, asVar(scale.light.shades[shade]));
    cb(`--brand-${shade}-d`, asVar(scale.dark.shades[shade]));
  }
  cb("--brand-contrast-l", asVar(scale.light.contrast));
  cb("--brand-contrast-d", asVar(scale.dark.contrast));
}

const BRAND_VAR_NAMES = [
  ...SHADES.flatMap((s) => [`--brand-${s}-l`, `--brand-${s}-d`]),
  "--brand-contrast-l",
  "--brand-contrast-d",
  "--brand-secondary-l",
  "--brand-secondary-d",
  "--chart-tiktok",
];

/**
 * The TikTok channel color, derived from the tenant primary with the same
 * geometry as the validated default pair (violet #7C3AED ↔ teal #0D9488):
 * identical lightness and chroma (inside both modes' chart-palette bands)
 * and the hue rotated by the same -111° the default pair has, so any
 * tenant's Shopify/TikTok series keep the hue distance that passed the
 * color-vision and contrast checks. A near-gray primary has no usable hue
 * and falls back to the stock teal.
 */
function channelAccent(primaryHex: string): string | null {
  const rgb = hexToRgb(primaryHex);
  if (!rgb) return null;
  const anchor = rgbToOklch(rgb);
  const hue = anchor.c < 0.02 ? 183 : (anchor.h - 111 + 360) % 360;
  const [r, g, b] = rgbTo255(oklchToRgb({ l: 0.64, c: 0.106, h: hue }));
  return `rgb(${r} ${g} ${b})`;
}

/**
 * Derives a full light+dark shade scale from the tenant's primary color and
 * writes it as inline CSS variables on <html>. The stylesheet's :root/.dark
 * indirection (--brand-N -> --brand-N-l / --brand-N-d) picks the right mode.
 * Invalid/missing hex leaves the stylesheet defaults untouched.
 */
export function applyBrandTheme({ primary, secondary }: BrandColors): void {
  const light = buildScale(primary, "light");
  const dark = buildScale(primary, "dark");
  if (!light || !dark) return;
  const style = document.documentElement.style;
  forEachVar({ light, dark }, (name, value) => style.setProperty(name, value));
  const tiktok = channelAccent(primary);
  if (tiktok) style.setProperty("--chart-tiktok", tiktok);
  const sec = secondaryTriplets(secondary || DEFAULT_BRAND.secondary);
  if (sec) {
    style.setProperty("--brand-secondary-l", asVar(sec.light));
    style.setProperty("--brand-secondary-d", asVar(sec.dark));
  }
}

/**
 * The white-label portal's theme, if any — the floor every reset falls back
 * to instead of the Reveshare defaults.
 *
 * Two layers, because branding has two sources (see usePortalBranding): the
 * page host (a `<slug>.reveshare.com` portal, known before login) and the
 * signed-in affiliate's scope (their home brand, known inside the affiliate
 * shell). The host wins when both are set: it is the address the visitor
 * can see. Each layer is owned by one mounted PortalBrandingTheme and cleared
 * when it unmounts.
 */
type PortalThemeLayer = "host" | "scope";
const portalLayers: Record<PortalThemeLayer, BrandColors | null> = {
  host: null,
  scope: null,
};

// Surfaces that must never wear a brand (the admin and sales panels) hold a
// suspension while mounted; the portal layers stay registered underneath so
// the theme returns the moment the surface unmounts.
let suspensions = 0;

function portalBaseTheme(): BrandColors | null {
  if (suspensions > 0) return null;
  return portalLayers.host ?? portalLayers.scope;
}

/** Turns the portal theme off until the returned release function runs. */
export function suspendPortalTheme(): () => void {
  suspensions += 1;
  resetBrandTheme();
  return () => {
    suspensions = Math.max(0, suspensions - 1);
    resetBrandTheme();
  };
}

/** Sets (or clears, with null) one layer of the portal theme and re-derives. */
export function setPortalTheme(
  layer: PortalThemeLayer,
  colors: BrandColors | null
): void {
  portalLayers[layer] = colors;
  resetBrandTheme();
}

/**
 * Removes all runtime brand vars, falling back to the portal's theme when
 * this is a white-label portal and to the stylesheet defaults otherwise.
 *
 * Pages that skin themselves with a brand's colours (the public join page,
 * a brand's detail page) reset on unmount; on a portal that must land back
 * on the portal's colours, never on Reveshare violet.
 */
export function resetBrandTheme(): void {
  const style = document.documentElement.style;
  for (const name of BRAND_VAR_NAMES) style.removeProperty(name);
  const base = portalBaseTheme();
  if (base) applyBrandTheme(base);
}
